/**
 * Calibrating a scan from a laser distance meter.
 *
 * Asked for on 2026-09-11 (Josh approved "calibrate a scan from one laser
 * measurement"). A LiDAR room scan is good to a few centimetres; a handheld
 * laser meter is good to about ±2 mm. The surveyor (or the designer, later)
 * reads one wall with the laser, and the scan's figures are scaled so that
 * wall reads what the laser read.
 *
 * OPTIONAL, ALWAYS (Josh, 2026-09-11: "keep this option only if anyone needs
 * that level of accuracy, otherwise person can move on"). An uncalibrated scan
 * is not a problem and nothing treats it as one: `applyCalibration` with no
 * calibrations returns the scan's own figures, and `calibrationMarker` says
 * "LiDAR scan". The ordinary flow is ONE wall and ONE reading, correcting the
 * whole scan. A room-only scope and a second, CHECK reading are extras for
 * whoever wants them, never required.
 *
 * THE MATHS. factor = laser ÷ scanned length of the chosen span. Every length
 * the scan quotes is multiplied by the factor, every area by factor², every
 * volume by factor³. That is what a uniform scale error does, and a uniform
 * scale error is the only kind one reading can correct.
 *
 * WHOLE SCAN BY DEFAULT, PER ROOM IF WANTED. Scale error can accumulate across
 * a multi-room capture (tracking drifts as the phone walks from room to room),
 * so a calibration may instead be scoped to one room; a room's own
 * calibration overrides the whole-scan one for that room, and a room with
 * neither is left exactly as scanned.
 *
 * WHAT IS REFUSED, AND WHY (`CALIBRATION_RULES`):
 *
 *   - A factor 2% or more away from 1. RoomPlan places a wall's ends to a
 *     centimetre or two (Spreafico et al. 2021: 1.4–1.9 cm RMS against a
 *     terrestrial laser scanner; Canvas quote whole scans "within 1–2%" — see
 *     docs/research/tape-measure-2026-09-10.md). A reading 2% off a 4 m wall is
 *     8 cm: not a scale error but one misplaced corner, or a different span
 *     measured (to a door frame, a skirting, into a bay), or a typo. Scaling
 *     the whole room to match would put every OTHER wall out by as much. Past
 *     5% the message says so bluntly: wrong wall, or a slip of the units.
 *   - A reference span under 1.5 m. The factor inherits the scan's corner error
 *     divided by the span: a centimetre on a 1.5 m wall is already 0.7%, as big
 *     as the correction being made. The longest wall is the best reference.
 *   - (Only when the optional check reading is given.) A check reading more
 *     than max(10 mm, 0.5% of the check reading) away from
 *     the corrected scan. 10 mm is the smallest disagreement that means
 *     anything above two laser readings' own error (±2 mm each, plus 1 mm of
 *     display rounding each); 0.5% keeps the limit proportional on long walls
 *     (18 mm on a 3.6 m wall) and caps what the calibrated figures can claim
 *     at about 1% on an area. Beyond it the room is not out by one factor —
 *     "scan is uneven here — measure this wall or rescan" — and the
 *     calibration is refused rather than silently applied.
 *
 * THE RAW SCAN IS NEVER TOUCHED. `applyCalibration` returns a scaled COPY; the
 * capture passed in is not mutated, so the stored capture stays the evidence
 * of what the sensor recorded, and the readings that produced the factor are
 * kept beside it (migration 0040).
 *
 * SIZES, NOT THE PLAN'S FRAME. Every surface and object keeps its plan
 * position (transform x and z): its dimensions grow or shrink about its own
 * centre. Heights are scaled about the room's floor, so a sill or a wall top
 * above the floor scales with everything else. So:
 *
 *   - lengths, heights, areas and volumes of every element come out × f, f², f³;
 *   - heights above the floor (a sill, a mounting height) come out × f;
 *   - the plan's frame does not move, so a radiator, pipe or hand-drawn
 *     dimension placed on the plan in the scan's own coordinates stays exactly
 *     where it was put — whether or not the scan is calibrated, and after a
 *     calibration is removed;
 *   - what is NOT corrected: a distance measured between two elements across
 *     the plan (a hand-drawn dimension across the room) is read in the scan's
 *     frame, and two walls meeting at a corner overlap or fall short by
 *     (f − 1) × half their lengths — about 1.5 mm per metre at ×1.003, never
 *     more than 1 cm per metre (the 2% limit).
 *
 * WHO CALLS WHAT:
 *
 *   evaluateCalibration(rawCapture, request)   → factor, or why not (preview AND the API's gate)
 *   applyCalibration(rawCapture, calibrations) → the scaled capture + per-room provenance
 *   calibrationMarker(room)                    → "calibrated ×1.0071 from a laser reading (J. Ryder, 11 Sep)"
 *
 * Everything downstream of a capture — `scanRoomGroups`, `scanPlanRooms`,
 * `convertCapture`, `assembleDwelling` — takes `applyCalibration(...).capture`
 * unchanged, and gets calibrated figures out.
 */

import type {
  CapturedObject,
  CapturedRoom,
  CapturedSurface,
  RoomPlanCapture,
} from "./roomplan.js";
import { capturedRoomRef, capturedRooms } from "./roomplan.js";
import { settledRooms, splitBaseRef } from "./room-split.js";
import {
  heightAxisOf,
  isUsableTransform,
  magnitude,
  median,
  translationOf,
} from "./spatial.js";

/** A point on the plan, metres, in the RAW scan's frame (`PlanPoint`). */
export interface CalibrationPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * What the laser was held against. A wall's length (RoomPlan's own width for
 * that wall — the figure the Walls table prints), or any two points on the
 * plan (a span between two corners that are not one wall's ends).
 */
export type CalibrationSpan =
  | { readonly kind: "wall"; readonly elementId: string }
  | { readonly kind: "points"; readonly a: CalibrationPoint; readonly b: CalibrationPoint };

export interface CalibrationReading {
  readonly span: CalibrationSpan;
  /** The laser meter's reading, millimetres. */
  readonly laserMm: number;
}

export type CalibrationScope =
  | { readonly kind: "scan" }
  | { readonly kind: "room"; readonly roomRef: string };

export interface CalibrationRequest {
  readonly scope: CalibrationScope;
  readonly reference: CalibrationReading;
  /** Optional: a second reading on another wall that the corrected scan must agree with. */
  readonly check?: CalibrationReading | null;
}

/** See the file header for the reasoning behind every figure. */
export const CALIBRATION_RULES = {
  /** |factor − 1| at or beyond this is refused. */
  maxDeviation: 0.02,
  /** |factor − 1| at or beyond this is called what it almost certainly is: the wrong span or a typo. */
  implausibleDeviation: 0.05,
  /** The shortest span a factor is taken from, metres. */
  minReferenceM: 1.5,
  /** The check reading's tolerance: the larger of this… */
  checkToleranceFloorMm: 10,
  /** …and this fraction of the check reading. */
  checkToleranceFraction: 0.005,
  /** A laser reading outside (0, this] mm is not a room measurement. */
  maxLaserMm: 30_000,
} as const;

export type CalibrationRefusalCode =
  | "laser-invalid"
  | "unknown-room"
  | "unknown-element"
  | "not-a-wall"
  | "wall-not-in-room"
  | "points-invalid"
  | "reference-too-short"
  | "factor-implausible"
  | "factor-out-of-range"
  | "check-same-span"
  | "check-not-in-room"
  | "check-failed";

/** A span, resolved against the capture. */
export interface CalibrationSpanResult {
  readonly span: CalibrationSpan;
  /** "Wall 3, Room 2" or "2 points on the plan". */
  readonly label: string;
  /** The room the span's wall is in; null for a two-point span. */
  readonly roomRef: string | null;
  /** The raw scan's length for the span, metres. */
  readonly scannedM: number;
  readonly laserMm: number;
}

export interface CalibrationCheckResult extends CalibrationSpanResult {
  /** The scanned length × the factor, metres. */
  readonly correctedM: number;
  /** Corrected minus laser, millimetres; negative when the corrected scan is short. */
  readonly residualMm: number;
  readonly toleranceMm: number;
  readonly withinTolerance: boolean;
}

export type CalibrationEvaluation =
  | {
      readonly ok: true;
      readonly factor: number;
      readonly reference: CalibrationSpanResult;
      readonly check: CalibrationCheckResult | null;
    }
  | {
      readonly ok: false;
      readonly code: CalibrationRefusalCode;
      readonly message: string;
      /** Present once the reference resolved, so a preview can still show the figure. */
      readonly factor?: number;
      readonly reference?: CalibrationSpanResult;
      readonly check?: CalibrationCheckResult;
    };

/** The check reading's tolerance for a given laser figure, millimetres. */
export function checkToleranceMm(laserMm: number): number {
  return Math.max(
    CALIBRATION_RULES.checkToleranceFloorMm,
    CALIBRATION_RULES.checkToleranceFraction * laserMm,
  );
}

interface WallEntry {
  readonly id: string;
  readonly kind: "wall" | "window" | "door" | "opening" | "floor" | "object";
  readonly label: string;
  readonly roomRef: string;
  readonly roomLabel: string;
  readonly widthM: number;
}

interface CaptureIndex {
  readonly rooms: ReadonlyMap<string, { readonly index: number; readonly label: string }>;
  readonly elements: ReadonlyMap<string, WallEntry>;
}

/**
 * Every element of the capture by id, labelled the way `scanRoomGroups` labels
 * it ("Wall 3", "Room 2"), so a message names the wall a person sees in the
 * table.
 */
function indexCapture(capture: RoomPlanCapture): CaptureIndex {
  const rooms = new Map<string, { index: number; label: string }>();
  const elements = new Map<string, WallEntry>();
  capturedRooms(capture).forEach((room, index) => {
    const roomRef = capturedRoomRef(room, index);
    const roomLabel = `Room ${index + 1}`;
    rooms.set(roomRef, { index, label: roomLabel });
    const add = (
      list: readonly { readonly identifier: string; readonly dimensions?: readonly number[] }[] | undefined,
      kind: WallEntry["kind"],
      singular: string,
    ): void => {
      (list ?? []).forEach((item, i) => {
        if (typeof item.identifier !== "string" || elements.has(item.identifier)) return;
        const width = item.dimensions?.[0];
        elements.set(item.identifier, {
          id: item.identifier,
          kind,
          label: `${singular} ${i + 1}`,
          roomRef,
          roomLabel,
          widthM: typeof width === "number" && Number.isFinite(width) ? width : 0,
        });
      });
    };
    add(room.walls, "wall", "Wall");
    add(room.windows, "window", "Window");
    add(room.doors, "door", "Door");
    add(room.openings, "opening", "Opening");
    add(room.floors, "floor", "Floor");
    add(room.objects, "object", "Object");
  });
  return { rooms, elements };
}

type Resolved =
  | { readonly ok: true; readonly result: CalibrationSpanResult }
  | { readonly ok: false; readonly code: CalibrationRefusalCode; readonly message: string };

const metres = (m: number): string => `${m.toFixed(3)} m`;
const percent = (fraction: number): string => `${(Math.abs(fraction) * 100).toFixed(1)}%`;

function finitePoint(p: CalibrationPoint | undefined): p is CalibrationPoint {
  return (
    p !== undefined && typeof p.x === "number" && Number.isFinite(p.x) &&
    typeof p.y === "number" && Number.isFinite(p.y)
  );
}

function resolveReading(
  index: CaptureIndex,
  reading: CalibrationReading,
  which: "reference" | "check",
): Resolved {
  const laser = reading.laserMm;
  if (typeof laser !== "number" || !Number.isFinite(laser) || laser <= 0 || laser > CALIBRATION_RULES.maxLaserMm) {
    return {
      ok: false,
      code: "laser-invalid",
      message:
        `The ${which} laser reading must be a length in millimetres, more than 0 and at most ` +
        `${CALIBRATION_RULES.maxLaserMm.toLocaleString("en-GB")} mm.`,
    };
  }
  const span = reading.span;
  if (span.kind === "points") {
    if (!finitePoint(span.a) || !finitePoint(span.b)) {
      return { ok: false, code: "points-invalid", message: `The ${which} span needs two points, each with a finite x and y in metres.` };
    }
    const scannedM = Math.hypot(span.b.x - span.a.x, span.b.y - span.a.y);
    return {
      ok: true,
      result: { span, label: "2 points on the plan", roomRef: null, scannedM, laserMm: laser },
    };
  }
  const element = index.elements.get(span.elementId);
  if (!element) {
    return {
      ok: false,
      code: "unknown-element",
      message:
        `This scan has no wall "${span.elementId}". The survey may have been re-scanned since ` +
        `the wall was picked — pick it again.`,
    };
  }
  if (element.kind !== "wall") {
    return {
      ok: false,
      code: "not-a-wall",
      message:
        `${element.label} (${element.roomLabel}) is a ${element.kind}, not a wall. Calibrate from ` +
        `a wall's length, or from two points on the plan.`,
    };
  }
  return {
    ok: true,
    result: {
      span,
      label: `${element.label}, ${element.roomLabel}`,
      roomRef: element.roomRef,
      scannedM: element.widthM,
      laserMm: laser,
    },
  };
}

function sameSpan(a: CalibrationSpan, b: CalibrationSpan): boolean {
  if (a.kind === "wall" && b.kind === "wall") return a.elementId === b.elementId;
  if (a.kind === "points" && b.kind === "points") {
    const eq = (p: CalibrationPoint, q: CalibrationPoint): boolean =>
      Math.abs(p.x - q.x) < 1e-6 && Math.abs(p.y - q.y) < 1e-6;
    return (eq(a.a, b.a) && eq(a.b, b.b)) || (eq(a.a, b.b) && eq(a.b, b.a));
  }
  return false;
}

/**
 * Whether a request would be accepted, and the factor it produces.
 *
 * Always evaluated against the RAW capture, never a calibrated one: the factor
 * is laser ÷ what the sensor recorded. The portal calls this for its live
 * preview and the API calls it again, server-side, against the stored capture
 * before anything is saved — one function, so the preview and the gate cannot
 * disagree.
 */
export function evaluateCalibration(
  capture: RoomPlanCapture,
  request: CalibrationRequest,
): CalibrationEvaluation {
  const index = indexCapture(capture);
  const scope = request.scope;
  let scopeLabel = "the whole scan";
  if (scope.kind === "room") {
    const room = index.rooms.get(scope.roomRef);
    if (!room) {
      return {
        ok: false,
        code: "unknown-room",
        message:
          `This scan has no room "${scope.roomRef}". The survey may have been re-scanned since ` +
          `the room was picked — pick it again.`,
      };
    }
    scopeLabel = room.label;
  }

  const ref = resolveReading(index, request.reference, "reference");
  if (!ref.ok) return { ok: false, code: ref.code, message: ref.message };
  const reference = ref.result;

  if (scope.kind === "room" && reference.roomRef !== null && reference.roomRef !== scope.roomRef) {
    return {
      ok: false,
      code: "wall-not-in-room",
      message:
        `${reference.label} is not in ${scopeLabel}. A room's calibration is measured on one of ` +
        `its own walls — pick one of ${scopeLabel}'s, or calibrate the whole scan instead.`,
      reference,
    };
  }

  if (!(reference.scannedM >= CALIBRATION_RULES.minReferenceM)) {
    return {
      ok: false,
      code: "reference-too-short",
      message:
        `${reference.label} is ${metres(reference.scannedM)} in the scan. Use a span at least ` +
        `${CALIBRATION_RULES.minReferenceM} m long: on anything shorter, a corner a centimetre out ` +
        `in the scan moves the factor by more than the correction being made. The longest wall ` +
        `is the best reference.`,
      reference,
    };
  }

  const factor = reference.laserMm / 1000 / reference.scannedM;
  const deviation = factor - 1;
  // Compared in whole parts per million so the 2% boundary is decided the same
  // way on every machine, not by the last bit of a division.
  const deviationPpm = Math.round(Math.abs(deviation) * 1e6);
  const longer = deviation > 0 ? "longer" : "shorter";
  if (deviationPpm >= Math.round(CALIBRATION_RULES.implausibleDeviation * 1e6)) {
    return {
      ok: false,
      code: "factor-implausible",
      message:
        `The laser reading (${reference.laserMm} mm) is ${percent(deviation)} ${longer} than the ` +
        `scanned ${metres(reference.scannedM)} for ${reference.label}. A LiDAR scan is not that far ` +
        `out: this is almost certainly a different span from the one picked (another wall, or to a ` +
        `door frame or skirting), or a typo — check the wall and the figure, and its units.`,
      factor,
      reference,
    };
  }
  if (deviationPpm >= Math.round(CALIBRATION_RULES.maxDeviation * 1e6)) {
    return {
      ok: false,
      code: "factor-out-of-range",
      message:
        `The laser reading (${reference.laserMm} mm) is ${percent(deviation)} ${longer} than the ` +
        `scanned ${metres(reference.scannedM)} for ${reference.label} — more than a scan's scale is ` +
        `out by. One end of this wall is probably misplaced in the scan, and scaling ${scopeLabel} ` +
        `to match would put every other wall out instead. Check the reading; if it stands, ` +
        `measure this wall directly or rescan.`,
      factor,
      reference,
    };
  }

  const checkReading = request.check ?? null;
  if (checkReading === null) return { ok: true, factor, reference, check: null };

  const chk = resolveReading(index, checkReading, "check");
  if (!chk.ok) return { ok: false, code: chk.code, message: chk.message, factor, reference };
  const checkSpan = chk.result;
  if (sameSpan(checkSpan.span, reference.span)) {
    return {
      ok: false,
      code: "check-same-span",
      message:
        `The check reading must be on a different wall from the reference: the same wall read ` +
        `twice agrees by construction and proves nothing about the rest of ${scopeLabel}.`,
      factor,
      reference,
    };
  }
  if (scope.kind === "room" && checkSpan.roomRef !== null && checkSpan.roomRef !== scope.roomRef) {
    return {
      ok: false,
      code: "check-not-in-room",
      message:
        `${checkSpan.label} is not in ${scopeLabel}. The check proves this room's correction, so ` +
        `it is read on another of ${scopeLabel}'s own walls.`,
      factor,
      reference,
    };
  }

  const correctedM = checkSpan.scannedM * factor;
  const residualMm = correctedM * 1000 - checkSpan.laserMm;
  const toleranceMm = checkToleranceMm(checkSpan.laserMm);
  const withinTolerance = Math.abs(residualMm) <= toleranceMm;
  const check: CalibrationCheckResult = {
    ...checkSpan,
    correctedM,
    residualMm,
    toleranceMm,
    withinTolerance,
  };
  if (!withinTolerance) {
    return {
      ok: false,
      code: "check-failed",
      message:
        `Scan is uneven here — measure this wall or rescan. Corrected by ×${factor.toFixed(4)}, ` +
        `${checkSpan.label} reads ${metres(correctedM)} but the laser reads ` +
        `${metres(checkSpan.laserMm / 1000)}: ${Math.abs(residualMm).toFixed(0)} mm apart, more than ` +
        `the ${toleranceMm.toFixed(0)} mm tolerance. ${scopeLabel === "the whole scan" ? "The scan" : scopeLabel} ` +
        `is not out by one factor, so one factor cannot correct it.`,
      factor,
      reference,
      check,
    };
  }
  return { ok: true, factor, reference, check };
}

// ---------------------------------------------------------------- applying

/** Who took the readings and when — what the provenance marker prints. */
export interface CalibrationProvenance {
  /** The person's full name as recorded, e.g. "Josh Ryder". */
  readonly byName: string | null;
  /** ISO timestamp the calibration was recorded. */
  readonly at: string;
}

/** A calibration as stored: what `applyCalibration` needs of it. */
export interface AppliedCalibration {
  readonly id: string;
  readonly scope: CalibrationScope;
  readonly factor: number;
  readonly provenance?: CalibrationProvenance;
}

export interface RoomCalibration {
  readonly roomIndex: number;
  /** `capturedRoomRef` — the room's identity, as `ScanRoomGroup.externalRef` carries it. */
  readonly roomRef: string;
  /** 1 when the room is uncalibrated. */
  readonly factor: number;
  /** Where the factor came from: the room's own calibration, the whole scan's, or neither. */
  readonly source: "room" | "scan" | "none";
  readonly calibration: AppliedCalibration | null;
}

export interface IgnoredCalibration {
  readonly calibration: AppliedCalibration;
  readonly reason: string;
}

export interface CalibratedCapture {
  /** A scaled copy. The capture passed in is untouched. */
  readonly capture: RoomPlanCapture;
  /** One entry per room, in capture order — `rooms[i]` is `ScanRoomGroup.index === i`. */
  readonly rooms: readonly RoomCalibration[];
  /** Calibrations that did not apply: a room not in this capture, or a factor outside the rules. */
  readonly ignored: readonly IgnoredCalibration[];
}

/** Captures this module produced, so a second application is refused rather than compounded. */
const CALIBRATED = new WeakSet<object>();

/** VERTICAL within ~8°, as `scan-plan.ts` counts it. */
function isVerticalAxis(transform: readonly number[]): boolean {
  const axis = heightAxisOf(transform);
  const length = magnitude(axis);
  return Number.isFinite(length) && length > 1e-6 && Math.abs(axis.y) / length >= 0.99;
}

/**
 * The room's floor, world Y: the flat floor surfaces' centres, else the
 * bottoms of its walls. Undefined when neither is available, and heights are
 * then scaled about each element's own centre instead.
 */
function floorYOf(room: CapturedRoom): number | undefined {
  const floors = (room.floors ?? [])
    .filter((f) => isUsableTransform(f.transform))
    .map((f) => translationOf(f.transform).y);
  const fromFloors = median(floors);
  if (fromFloors !== undefined) return fromFloors;
  const bases = (room.walls ?? [])
    .filter((w) => isUsableTransform(w.transform) && isVerticalAxis(w.transform))
    .map((w) => {
      const h = w.dimensions?.[1];
      return translationOf(w.transform).y - (typeof h === "number" && Number.isFinite(h) ? h : 0) / 2;
    });
  return median(bases);
}

const scaled = (values: readonly number[], f: number): number[] =>
  values.map((v) => (typeof v === "number" && Number.isFinite(v) ? v * f : v));

function liftTransform(
  transform: readonly number[] | undefined,
  f: number,
  floorY: number | undefined,
): readonly number[] | undefined {
  if (floorY === undefined || !isUsableTransform(transform)) return transform;
  const next = [...transform];
  next[13] = floorY + f * ((transform[13] as number) - floorY);
  return next;
}

function scaleSurface(surface: CapturedSurface, f: number, floorY: number | undefined): CapturedSurface {
  const transform = liftTransform(surface.transform, f, floorY);
  return {
    ...surface,
    dimensions: Array.isArray(surface.dimensions) ? scaled(surface.dimensions, f) : surface.dimensions,
    ...(transform !== undefined ? { transform } : {}),
    ...(Array.isArray(surface.polygonCorners)
      ? { polygonCorners: surface.polygonCorners.map((p) => (Array.isArray(p) ? scaled(p, f) : p)) }
      : {}),
  };
}

function scaleObject(object: CapturedObject, f: number, floorY: number | undefined): CapturedObject {
  const transform = liftTransform(object.transform, f, floorY);
  return {
    ...object,
    ...(Array.isArray(object.dimensions) ? { dimensions: scaled(object.dimensions, f) } : {}),
    ...(transform !== undefined ? { transform } : {}),
  };
}

function scaleRoom(room: CapturedRoom, f: number): CapturedRoom {
  if (f === 1) return room;
  const floorY = floorYOf(room);
  const surfaces = (list: readonly CapturedSurface[] | undefined) =>
    list === undefined ? undefined : list.map((s) => scaleSurface(s, f, floorY));
  const walls = surfaces(room.walls);
  const doors = surfaces(room.doors);
  const windows = surfaces(room.windows);
  const openings = surfaces(room.openings);
  const floors = surfaces(room.floors);
  return {
    ...room,
    ...(walls ? { walls } : {}),
    ...(doors ? { doors } : {}),
    ...(windows ? { windows } : {}),
    ...(openings ? { openings } : {}),
    ...(floors ? { floors } : {}),
    ...(room.objects ? { objects: room.objects.map((o) => scaleObject(o, f, floorY)) } : {}),
  };
}

function withinRules(factor: number): boolean {
  if (typeof factor !== "number" || !Number.isFinite(factor) || factor <= 0) return false;
  return Math.round(Math.abs(factor - 1) * 1e6) < Math.round(CALIBRATION_RULES.maxDeviation * 1e6);
}

/**
 * The scan with its calibrations applied — see the file header.
 *
 * `calibrations` are the ones in force for THIS capture (the API's
 * `applies: true`); a room's own calibration overrides the whole-scan one. If
 * two are given for the same scope the later one in the list wins, so pass
 * them oldest first. A calibration naming a room this capture does not have,
 * or carrying a factor the rules would refuse, is not applied and is listed in
 * `ignored` with the reason — never applied on trust.
 *
 * Pass the RAW capture. Passing a capture this function returned throws: a
 * second application would compound the factor and nothing downstream could
 * tell.
 */
export function applyCalibration(
  capture: RoomPlanCapture,
  calibrations: readonly AppliedCalibration[],
): CalibratedCapture {
  if (CALIBRATED.has(capture)) {
    throw new Error(
      "applyCalibration was given a capture it had already calibrated. Pass the raw capture: " +
        "applying twice would compound the factor.",
    );
  }
  const rooms = capturedRooms(capture);
  const refs = rooms.map((room, i) => capturedRoomRef(room, i));
  // A room calibrated before its scan was told apart into rooms (room-split.ts)
  // still calibrates every room it became.
  const known = new Set([...refs, ...refs.map(splitBaseRef)]);
  const ignored: IgnoredCalibration[] = [];
  let scanWide: AppliedCalibration | null = null;
  const perRoom = new Map<string, AppliedCalibration>();

  for (const calibration of calibrations) {
    if (!withinRules(calibration.factor)) {
      ignored.push({
        calibration,
        reason: `factor ${String(calibration.factor)} is outside the ±${CALIBRATION_RULES.maxDeviation * 100}% a calibration may apply`,
      });
      continue;
    }
    if (calibration.scope.kind === "scan") {
      scanWide = calibration;
    } else if (known.has(calibration.scope.roomRef)) {
      perRoom.set(calibration.scope.roomRef, calibration);
    } else {
      ignored.push({
        calibration,
        reason: `this capture has no room "${calibration.scope.roomRef}"`,
      });
    }
  }

  const statuses: RoomCalibration[] = refs.map((roomRef, roomIndex) => {
    const own = perRoom.get(roomRef) ?? perRoom.get(splitBaseRef(roomRef));
    if (own) return { roomIndex, roomRef, factor: own.factor, source: "room", calibration: own };
    if (scanWide) return { roomIndex, roomRef, factor: scanWide.factor, source: "scan", calibration: scanWide };
    return { roomIndex, roomRef, factor: 1, source: "none", calibration: null };
  });

  const scaledRooms = settledRooms(rooms.map((room, i) => scaleRoom(room, statuses[i]!.factor)));
  // A bare room told apart into several comes back as a structure of them.
  const out: RoomPlanCapture =
    "rooms" in capture && Array.isArray(capture.rooms)
      ? { ...capture, rooms: scaledRooms }
      : scaledRooms.length > 1
        ? { rooms: scaledRooms }
        : (scaledRooms[0] ?? capture);
  // A capture no room of which was scaled is still marked, so a caller holding
  // "the calibrated capture" never has it calibrated again.
  const result = out === capture ? { ...capture } : out;
  CALIBRATED.add(result);
  return { capture: result, rooms: statuses, ignored };
}

// ------------------------------------------------------------- provenance

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "11 Sep", in UK time — spelled out here because ICU's en-GB month names vary ("Sept"). */
function dayMonth(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    day: "numeric",
    month: "numeric",
  }).formatToParts(date);
  const day = parts.find((p) => p.type === "day")?.value;
  const month = Number(parts.find((p) => p.type === "month")?.value);
  if (!day || !(month >= 1 && month <= 12)) return null;
  return `${day} ${MONTHS[month - 1]}`;
}

/** "Josh Ryder" → "J. Ryder"; a single name stands as it is. */
function shortName(name: string | null | undefined): string | null {
  const words = (name ?? "").trim().split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return null;
  if (words.length === 1) return words[0]!;
  return `${words[0]!.charAt(0).toUpperCase()}. ${words[words.length - 1]}`;
}

/** "×1.0071" */
export function formatCalibrationFactor(factor: number): string {
  return `×${factor.toFixed(4)}`;
}

/**
 * What a calibrated figure says about itself, one line:
 *
 *   calibrated ×1.0071 from a laser reading (J. Ryder, 11 Sep)                ← the whole scan
 *   calibrated ×1.0071 from a laser reading for this room (J. Ryder, 11 Sep)  ← a room's own
 *   LiDAR scan                                                               ← uncalibrated
 *
 * One function so the Survey page, the exports and anything else that prints
 * a calibrated figure say it the same way.
 */
export function calibrationMarker(room: Pick<RoomCalibration, "factor" | "source" | "calibration"> | null | undefined): string {
  if (!room || room.source === "none" || room.calibration === null) return "LiDAR scan";
  const provenance = room.calibration.provenance;
  const who = shortName(provenance?.byName);
  const when = provenance ? dayMonth(provenance.at) : null;
  const parts = [who, when].filter((p): p is string => p !== null);
  // The whole scan is the ordinary case and needs no qualifier; a room's own
  // calibration (the optional extra) says so.
  const scope = room.source === "room" ? " for this room" : "";
  return (
    `calibrated ${formatCalibrationFactor(room.factor)} from a laser reading${scope}` +
    (parts.length > 0 ? ` (${parts.join(", ")})` : "")
  );
}
